import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Menu,
  X,
  Users2,
  UserCog,
  LogOut,
  Shield,
  Mail,
  Megaphone,
  Activity,
  Newspaper,
  ImageIcon,
} from 'lucide-react';
import { authAction, authSelector } from '../features/auth/slice';
import useAuthInit from '../hooks/useAuthInit';
import { PUBLIC_BASE } from '../pages/public/config';
import UserAvatar from './UserAvatar';
import { useMessage } from '../contexts/MessageContext';

const LOGO_URL =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/logo.png';

function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector(authSelector.selectAuthState);
  useAuthInit();

  const [mobileOpen, setMobileOpen] = useState(false);
  const { unreadCount, openInbox } = useMessage() || {};

  const logout = () => {
    dispatch(authAction.logout());
    localStorage.removeItem('token');
    navigate(`${PUBLIC_BASE || '/'}`);
  };

  const isSuperAdmin = me?.superAdminYn === 'Y';

  const adminLinks = [
    { to: `${PUBLIC_BASE}/admin/members`, label: '일반회원관리', icon: Users2 },
    { to: `${PUBLIC_BASE}/admin/messages`, label: '일괄메시지', icon: Mail },
    { to: `${PUBLIC_BASE}/admin/popups`, label: '팝업관리', icon: Megaphone },
    { to: `${PUBLIC_BASE}/admin/card-news`, label: '카드뉴스', icon: Newspaper, superAdminOnly: true },
    { to: `${PUBLIC_BASE}/admin/access-logs`, label: '접속기록', icon: Activity, superAdminOnly: true },
    { to: `${PUBLIC_BASE}/admin/image-edit`, label: '이미지편집', icon: ImageIcon, superAdminOnly: true },
  ].filter((link) => !link.superAdminOnly || isSuperAdmin);

  const isActive = (to) => pathname === to || pathname.startsWith(to + '/');

  return (
    <div className="min-h-dvh bg-[#0d1526] flex flex-col">
      <header className="sticky top-0 z-40 bg-[#0d1526]/95 backdrop-blur-xl">
        {/* Row 1: 로고 · 유저 정보 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="h-[52px] flex items-center gap-5">
            {/* 로고 */}
            <Link to={`${PUBLIC_BASE}/admin/members`} className="shrink-0 flex items-center gap-2">
              <img src={LOGO_URL} alt="impink" className="h-7 w-auto" />
              <span className="flex items-center gap-1 text-amber-400 text-xs font-bold">
                <Shield size={13} />
                관리자
              </span>
            </Link>

            {/* 우측: 유저 정보 */}
            <div className="hidden sm:flex items-center gap-2 ml-auto shrink-0">
              {me && (
                <div className="flex items-center gap-2">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType || 'client'}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white max-w-[100px] truncate">{me.name}</span>
                  <button
                    title="메시지"
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => openInbox?.()}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    title="프로필 수정"
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => navigate(me?.userType === 'member' ? `${PUBLIC_BASE}/mypage` : '/EditProfile')}>
                    <UserCog size={15} />
                  </button>
                  <button
                    title="로그아웃"
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={logout}>
                    <LogOut size={15} />
                  </button>
                </div>
              )}
            </div>

            {/* 모바일 햄버거 */}
            <button
              className="sm:hidden ml-auto p-2 text-slate-400 hover:text-white shrink-0"
              onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* 행 구분선 */}
        <div className="border-b border-white/[0.05]" />

        {/* Row 2: 데스크탑 네비게이션 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="hidden sm:flex items-center -ml-3 -mb-px">
            {adminLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium transition-colors border-b-2 ${
                  isActive(to)
                    ? 'text-white border-amber-400'
                    : 'text-slate-500 border-transparent hover:text-slate-300'
                }`}>
                <Icon size={14} />
                {label}
              </Link>
            ))}

            {/* 우측: 사이트 전환 버튼 */}
            <div className="flex items-center gap-2 ml-auto">
              <Link
                to={PUBLIC_BASE || '/'}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/15 text-sky-400 hover:bg-sky-500/30 transition-colors">
                홈페이지
              </Link>
              {me?.userType === 'client' && (
                <Link
                  to="/dashboard"
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-400 hover:bg-rose-500/30 transition-colors">
                  정산시스템
                </Link>
              )}
            </div>
          </nav>
        </div>

        {/* 하단 구분선 */}
        <div className="border-b border-white/[0.06]" />

        {/* 모바일 드롭다운 */}
        {mobileOpen && (
          <nav className="sm:hidden border-t border-white/10 px-4 py-3 flex flex-col gap-1 bg-[#0d1526]/95 backdrop-blur">
            {/* 모바일 유저 */}
            {me && (
              <div className="flex items-center justify-between px-3 py-3 mb-2 border-b border-white/5">
                <div className="flex items-center gap-2 min-w-0">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType || 'client'}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white truncate">{me.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => { openInbox?.(); setMobileOpen(false); }}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => { navigate(me?.userType === 'member' ? `${PUBLIC_BASE}/mypage` : '/EditProfile'); setMobileOpen(false); }}>
                    <UserCog size={15} />
                  </button>
                  <button
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={() => { logout(); setMobileOpen(false); }}>
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            )}

            {adminLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive(to)
                    ? 'text-amber-400 bg-amber-500/10'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}>
                <Icon size={15} />
                {label}
              </Link>
            ))}

            {/* 모바일 사이트 전환 */}
            <div className="flex items-center gap-2 px-3 pt-3 mt-2 border-t border-white/5">
              <Link
                to={PUBLIC_BASE || '/'}
                onClick={() => setMobileOpen(false)}
                className="flex-1 text-center px-3 py-2 rounded-lg text-xs font-semibold bg-sky-500/15 text-sky-400 hover:bg-sky-500/30 transition-colors">
                홈페이지
              </Link>
              {me?.userType === 'client' && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center px-3 py-2 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-400 hover:bg-rose-500/30 transition-colors">
                  정산시스템
                </Link>
              )}
            </div>
          </nav>
        )}
      </header>

      {/* 콘텐츠 */}
      <main className="flex-1 text-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
          <Outlet />
        </div>
      </main>

      {/* 푸터 */}
      <footer className="border-t border-white/5 bg-[#0a101e]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <p className="text-slate-600 text-xs text-center">
            p!nk 관리자 페이지
          </p>
        </div>
      </footer>

    </div>
  );
}

export default AdminLayout;
